import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { Badge } from '../ui/badge'
import { useFormatters } from '../../lib/format'

/**
 * EmailDelivery is the state the backend records for one outbound e-mail —
 * `intervention.properties.os_email` and `issue.properties.workflow` share this
 * shape (see internal/handlers/emaildelivery).
 */
export interface EmailDelivery {
    job_uid?: string
    status?: string
    queued_at?: string
    sent_at?: string
    status_at?: string
    message_id?: string
    error?: string
    to?: string[]
}

/**
 * EmailDeliveryChip tells the truth about an ordre de service e-mail: queued,
 * sent at a time, or failed with the reason — always with a link to the job
 * that carries it.
 *
 * It exists because the previous UI showed a `sent_at` the producer had written
 * optimistically before anything left, so a work order that never reached the
 * provider looked identical to one that did (spec 2026-08-03-01 §3). A failed
 * OS e-mail is an action item, not a silent field, so the failure renders
 * destructive and carries its message.
 */
export function EmailDeliveryChip({
    org,
    delivery,
    className,
}: {
    org: string
    delivery?: EmailDelivery | null
    className?: string
}) {
    const { t } = useTranslation('workorders')
    const { formatDate } = useFormatters()

    if (!delivery || !delivery.status) return null

    const status = delivery.status
    // "awaiting" is the ticket-side spelling of a successful send (the ticket is
    // then awaiting the provider); both mean the mail left.
    const isSent = status === 'sent' || status === 'awaiting' || !!delivery.sent_at
    const isFailed = status === 'failed'

    let label: string
    let variant: 'outline' | 'secondary' | 'destructive' = 'outline'
    if (isFailed) {
        label = t('delivery.failed')
        variant = 'destructive'
    } else if (isSent) {
        label = t('delivery.sent', {
            time: delivery.sent_at
                ? formatDate(delivery.sent_at, { dateStyle: 'short', timeStyle: 'short' })
                : '',
        })
        variant = 'secondary'
    } else {
        label = t('delivery.queued')
    }

    return (
        <div className={`flex flex-col gap-1 ${className ?? ''}`} data-testid="os-email-delivery">
            <div className="flex flex-wrap items-center gap-2">
                <Badge variant={variant} data-testid="os-email-delivery-chip" data-status={status}>
                    {label}
                </Badge>
                {delivery.job_uid ? (
                    <Link
                        to="/$org/jobs/$jobUid"
                        params={{ org, jobUid: delivery.job_uid }}
                        className="text-xs text-primary hover:underline"
                        data-testid="os-email-job-link"
                    >
                        {t('delivery.view_job')}
                    </Link>
                ) : null}
            </div>
            {isFailed ? (
                <p className="text-xs text-destructive" data-testid="os-email-delivery-error">
                    {delivery.error || t('delivery.failed_hint')}
                </p>
            ) : null}
            {!isFailed && !isSent ? (
                <p className="text-xs text-muted-foreground" data-testid="os-email-delivery-hint">
                    {t('delivery.queued_hint')}
                </p>
            ) : null}
        </div>
    )
}
